import type { FC, MouseEvent, ReactElement } from 'react';
import { useRef, useState } from 'react';

import { useAudioTrigger } from '../../controllers/audioController/hooks';
import { useModifiers } from '../../styles/hooks/useModifiers';

import styles from './Tabs.module.css';

export interface TabProps {
  content: ReactElement;
  label: ReactElement;
}

export interface TabsProps {
  tabs: TabProps[];
  initActiveTab?: number;
  gameStyle?: boolean;
  className?: string;
  classNameContainer?: string;
  classNameLabel?: string;
  isOneWidth?: boolean;
  renderInactive?: boolean;
  onTabChange?: (tab: TabProps, event?: MouseEvent<HTMLDivElement>) => void | undefined;
}

export const Tabs: FC<TabsProps> = ({
  tabs,
  gameStyle,
  className,
  classNameContainer,
  classNameLabel,
  initActiveTab = 0,
  isOneWidth,
  renderInactive,
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState(initActiveTab);
  const ref = useRef<HTMLDivElement>(null);
  const mods = useModifiers(styles, 'tabs', { renderInactive }, { withBaseClass: true });
  const headerMods = useModifiers(styles, 'tabs__header', { isOneWidth }, { withBaseClass: true });

  const audioTrigger = useAudioTrigger();

  return (
    <div className={`${mods} ${gameStyle && styles.tabs_game} ${className}`}>
      <div className={`${headerMods} ${classNameLabel}`} data-testid="tabs-header">
        {tabs.map((tab, i) => (
          <div
            data-testid={activeTab === i ? 'tab-active' : 'tab'}
            key={i}
            className={`${styles.tabs__header__item} ${activeTab === i ? styles.tabs__header__item_active : ''}`}
            onClick={(event: MouseEvent<HTMLDivElement>): void => {
              setActiveTab(i);
              if (ref.current) {
                ref.current.scrollTo(0, 0);
              }
              audioTrigger.play({ name: 'menuClick' });
              if (onTabChange) {
                onTabChange(tab, event);
              }
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div className={`${styles.tabs__content} ${classNameContainer}`} ref={ref}>
        {renderInactive
          ? tabs.map((tab, i) => (
              <div
                className={`${styles.tabs__content_block} ${i === activeTab ? styles.tabs__content_block_visible : ''}`}
                key={i}
              >
                {tab.content}
              </div>
            ))
          : tabs[activeTab]?.content}
      </div>
    </div>
  );
};
